import React, { useState } from 'react';
import { Briefcase, Calendar, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const ExperienceTimeline = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const experiences = [
    {
      company: 'Citi Bank',
      location: 'Dallas, TX',
      title: 'Project Manager / Scrum Master',
      period: 'July 2025 - Jan 2026',
      duration: '7 months',
      highlights: [
        'Managed project schedules and budgets, ensuring on-time delivery for digital transformation initiatives',
        'Defined product strategy and roadmap for digital payments platform',
        'Led AEM project delivery and integration with Salesforce',
        'Implemented RAID framework for PI planning and dependency management',
        'Managed Azure migrations with comprehensive RACI matrix for CI/CD pipelines',
      ],
      technologies: ['Digital Payments', 'AEM', 'Salesforce', 'Azure', 'SAFe'],
    },
    {
      company: 'Bank of America',
      location: 'Charlotte, NC',
      title: 'Project Manager / Scrum Master',
      period: 'Oct 2020 - June 2025',
      duration: '4 years 9 months',
      highlights: [
        'Managed transaction systems and payment gateways (Zelle, Stripe) for digital banking platform',
        'Led SAFe PI Planning sessions for 5+ Agile teams',
        'Managed $5M+ projects using comprehensive RAID log framework',
        'Achieved 30% increase in delivery velocity through RACI matrix implementation',
        'Orchestrated Azure migration and CI/CD pipeline implementation',
        'Reduced production defect rates by 30% through integrated test cycles',
      ],
      technologies: ['Zelle', 'Stripe', 'Azure', 'Salesforce', 'AEM', 'Microservices'],
    },
    {
      company: 'HPE',
      location: 'Austin, TX',
      title: 'Project Manager / Scrum Master',
      period: 'Aug 2019 - Oct 2020',
      duration: '1 year 3 months',
      highlights: [
        'Facilitated Scrum ceremonies for cross-functional development teams',
        'Led cloud migration strategy for hybrid architecture payer systems',
        'Managed data warehouse modernization from legacy ETL to cloud-native analytics',
        'Established SLA/KPIs for claims, access, and provider workflows',
        'Integrated AI systems for CRAY projects',
      ],
      technologies: ['Azure', 'Cloud Migration', 'Payer Systems', 'AI Integration'],
    },
    {
      company: 'Rackspace',
      location: 'San Antonio, TX',
      title: 'Product Owner / Business Analyst / Scrum Master',
      period: 'Jan 2019 - Aug 2019',
      duration: '8 months',
      highlights: [
        'Defined product features and priorities for 3+ Scrum teams (25+ engineers)',
        'Created KPI dashboards and release metrics',
        'Led cloud migration and banking operations modernization project plans',
        'Coached Scrum Masters and Business Analysts on best practices',
      ],
      technologies: ['Cloud Infrastructure', 'Banking Operations', 'KPI Dashboards'],
    },
    {
      company: 'M&T Bank',
      location: 'Buffalo, NY',
      title: 'Project Manager / Scrum Master',
      period: 'Jan 2018 - Dec 2018',
      duration: '1 year',
      highlights: [
        'Achieved 25% improvement in sprint predictability',
        'Led organizational Agile adoption initiatives',
        'Managed Azure migration with RACI matrix for CI/CD pipeline',
        'Implemented integration patterns using APIM for SFCC basket calculation',
        'Conducted discovery workshops translating marketing strategies to technical specs',
      ],
      technologies: ['Azure', 'APIM', 'Agile Transformation', 'CI/CD'],
    },
    {
      company: 'USAA',
      location: 'San Antonio, TX',
      title: 'Project Program Manager & Scrum Master / Business Analyst',
      period: 'Jan 2017 - Dec 2017',
      duration: '1 year',
      highlights: [
        'Led digital banking transformation as Product Owner',
        'Ensured PCI DSS/PA DSS compliance for payment systems',
        'Led Agile transformation and coached development teams',
        'Implemented API integration patterns for microservices and Salesforce',
        'Performed quantitative analysis for fraud loss and authorization rates',
      ],
      technologies: ['PCI DSS', 'Digital Banking', 'Salesforce', 'API Integration'],
    },
    {
      company: 'Western Union',
      location: 'Montvale, NJ',
      title: 'Project Manager / Scrum Master',
      period: 'Aug 2015 - Jan 2017',
      duration: '1 year 6 months',
      highlights: [
        'Managed $5M Web/Mobile Application Development program',
        'Successfully transitioned team from Waterfall to Agile methodology',
        'Achieved 30% increase in team velocity for 20+ member team',
        'Managed RAID logs for PCI DSS compliance and technical dependencies',
        'Led product vision for retail POS transaction platform',
      ],
      technologies: ['Mobile Development', 'PCI DSS', 'Agile Transformation'],
    },
    {
      company: 'Anthem',
      location: 'Wallingford, CT',
      title: 'Product Owner / Scrum Master',
      period: 'Dec 2014 - Aug 2015',
      duration: '9 months',
      highlights: [
        'Defined product strategy for healthcare member portal and claims management',
        'Conducted UAT with healthcare teams',
        'Performed quantitative analysis for member engagement metrics',
        'Implemented automation framework for healthcare reporting',
        'Guided frontend engineering using jQuery',
      ],
      technologies: ['Healthcare Portal', 'Claims Management', 'jQuery', 'ETL'],
    },
    {
      company: 'Gordon Food Service',
      location: 'Grand Rapids, MI',
      title: 'Project Manager / Scrum Master',
      period: 'Jun 2012 - Dec 2014',
      duration: '2 years 7 months',
      highlights: [
        'Led Go to Market strategy for GFS Marketplace',
        'Reduced financial tracking analysis time by 30%',
        'Facilitated program boards and Scrum of Scrums for PI objectives',
        'Managed requirements gathering and system integration workflows',
        'Implemented test strategies and Jenkins CI/CD',
      ],
      technologies: ['E-commerce', 'Jenkins', 'CI/CD', 'jQuery'],
    },
    {
      company: 'Nokia',
      location: 'Carlsbad, CA',
      title: 'Project Manager / Business Analyst',
      period: 'Jun 2010 - May 2012',
      duration: '2 years',
      highlights: [
        'Managed PeopleSoft ERP upgrade and customization projects',
        'Designed and deployed Intranet solutions with training and support',
        'Created wireframes, personas, and process flow diagrams using Lean methodologies',
        'Developed SQL reporting using Cognos 8 and Tableau',
        'Conducted comprehensive testing (smoke, GUI, integration, functional, regression, UAT)',
      ],
      technologies: ['PeopleSoft ERP', 'Cognos 8', 'Tableau', 'SQL', 'Lean'],
    },
  ];

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section id="experience" className="py-20 bg-white" data-testid="experience-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="experience-title">
            Professional Journey
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600">16+ Years of Excellence in Project Management & Agile Leadership</p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-1 bg-blue-200"></div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className={`relative flex flex-col md:flex-row gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                data-testid={`experience-item-${index}`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg z-10"></div>

                {/* Content Card */}
                <div className={`flex-1 ml-20 md:ml-0 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100">
                    {/* Header */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-blue-600 mb-2">
                        <Briefcase className="w-5 h-5" />
                        <span className="font-semibold text-lg">{exp.company}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{exp.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {exp.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {exp.period}
                        </span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          {exp.duration}
                        </span>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className={`transition-all duration-300 ${expandedIndex === index ? 'max-h-[1000px]' : 'max-h-0 overflow-hidden'}`}>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-3">Key Achievements:</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          {exp.highlights.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-blue-600 mt-1">•</span>
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {exp.technologies.map((tech, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Expand Button */}
                    <button
                      onClick={() => toggleExpand(index)}
                      className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                      data-testid={`expand-btn-${index}`}
                    >
                      {expandedIndex === index ? (
                        <>
                          Show Less <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          Show More <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden md:block flex-1"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceTimeline;
